import { Target, Compass, HeartHandshake } from "lucide-react";
import aboutTeam from "@/assets/about-team.jpg";
import aboutCorporate from "@/assets/about-corporate.jpg";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4 mm-fade-up">
              About us
            </div>
            <h1 className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
              A talent partner built for the way modern engineering teams ship.
            </h1>
            <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              T3 Solutions Pvt Ltd is a staff augmentation and managed services company. We help product teams move faster by placing rigorously vetted engineers and specialists — on contract, remote, or full-time — exactly where they're needed.
            </p>
          </div>
          <div className="lg:col-span-5 mm-fade-up mm-delay-3">
            <img
              src={aboutTeam}
              alt="A collaborative engineering team"
              className="w-full h-[380px] lg:h-[440px] object-cover rounded-xl border border-slate-200"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {[
              {
                icon: Target,
                title: "What we do",
                body: "We provide elite software development talent and managed delivery for product engineering, digital transformation, application development, DevOps, QA, SAP Services, AI/ML, and data analytics.",
              },
              {
                icon: Compass,
                title: "How we work",
                body: "Hand-picked engineers. Rigorous screening. Tight feedback loops with your team. We embed people who own outcomes, not just tickets.",
              },
              {
                icon: HeartHandshake,
                title: "Why teams choose us",
                body: "Speed without compromise. Quality without bloat. A long-term partnership focused on the success of your roadmap — not ours.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`about-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-white p-8 lg:p-10 flex flex-col gap-5"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-blue-600 text-white">
                  <Icon size={20} />
                </span>
                <h3 className="font-heading text-xl font-medium text-slate-900">{title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={aboutCorporate}
              alt="Minimal corporate environment"
              className="w-full h-[440px] object-cover rounded-xl border border-slate-200"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-3">
              Our principles
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">
              Built around one idea: people make the product.
            </h2>
            <div className="mt-8 space-y-6 text-slate-600 leading-relaxed text-sm sm:text-base">
              <p>
                Every engagement starts by listening — to your goals, your stack, and the way your team works. From there, we line up talent who can integrate quickly, contribute meaningfully, and grow with your business.
              </p>
              <p>
                Whether you need a single senior engineer on a six-month engagement, a full team to deliver a managed program of work, or a permanent hire to anchor a critical role, we operate as an extension of your team — never a vendor at arm's length.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
